/* eslint-disble react/jsx-sort-props */

import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { SearchFilterContext } from '../../context/SearchFilterContext';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { Provider } from '../../models/Provider';
import {
  ProviderType,
  RecommendationDetails,
} from '../../models/ProviderDetails';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  returnSuppressFlag,
  suppressCostEstimatesForPolicies,
} from '../../utils/featureSuppress';
import {
  setResultIcon,
  setZoomLevel,
  updateIconSize,
} from '../../utils/map.utils';
import { getPrimaryPhone } from '../../utils/phone.utils';
import { getProviderSpecialtyOrOrgTypeCode } from '../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../utils/providerNames.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { CareCategories, Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { mobileOnly, notXlScreenOnly, phoneOnly } from '../ConstantsStyles';
import { CardInfo } from '../SnackCard/CardInfo';
import { SmartChoiceScoreCard } from '../SnackCard/SmartChoiceScoreCard';
import { BestMatchDetails } from './BestMatchDetails';
import { DataCard } from './DataCard';
import { DataContainer } from './DataCard.style';
import { DataCardSkeleton } from './DataCardSkeleton';
import { DataCardWithExternalLink } from './DataCardWithExternalLink';
import { GroupedPinDataCard } from './GroupedPinDataCard';
import { SmartChoiceDetails } from './SmartChoiceDetails';

type Props = {
  practitioner?: Provider;
  css?: any;
  sectionType?: string;
  updatePin?: (practitioner?: Provider) => void;
  isLoading?: boolean;
  fromMobileListView?: boolean;
  handleDirectionClick?: () => void;
  index?: number;
  scrollIntoView?: (a: number) => void;
  locationForAnalytics?: string;
  pageNumber?: number;
  map?: any;
  selectedFilters?: string;
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  usedAt?: string;
  popupContainer?: boolean;
  searchTerm?: string;
  navigateToDirections?: boolean;
  setNavigateToDirections?: (a: boolean) => void;
  mobileRouteView?: boolean;
  headers?: ResponseHeaders;
  surveyCount?: number;
  isNetworkAlertIndicator?: boolean;
  isMNR?: boolean;
  isInNetworkBadge?: boolean;
  isPopup?: boolean;
  badgeCardType?: string;
  isTieredPlan?: boolean;
  searchRadius?: number | string;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
  providerList?: Provider[];
  isGroupedLocationDrawer?: boolean;
  currentPage?: number;
  drawerContent?: any;
  isCheckboxDisabled?: any;
  setSelectedCheckbox?: any;
  addSelectedProvider?: any;
  adobeLinkTrackEventCheckBox?: any;
  setLastSelectedIndex?: any;
  selectedCheckbox?: any;
  compareShareToggle?: boolean;
  setIsCompareClicked?: any;
  maxBadgeCount?: number;
  recommendationPreferences?: any;
  isMapView?: boolean;
};

const getLinkPositionIndex = (linkPosition?: number) => {
  if (linkPosition !== undefined) {
    return linkPosition + 1;
  }
  return linkPosition;
};

export const DataCardContainer = ({
  popupContainer,
  practitioner = {},
  css = { width: '100%' },
  sectionType,
  updatePin,
  isLoading,
  fromMobileListView,
  handleDirectionClick,
  isMapView = false,
  index = 0,
  scrollIntoView,
  locationForAnalytics,
  pageNumber,
  map,
  selectedFilters,
  setRouteEndCoords,
  usedAt = '',
  searchTerm = '',
  navigateToDirections = false,
  setNavigateToDirections,
  mobileRouteView = false,
  headers = { correlationId: '' },
  isNetworkAlertIndicator,
  isMNR,
  isInNetworkBadge,
  isPopup = false,
  badgeCardType = 'data-card',
  isTieredPlan,
  searchRadius,
  expandedProviderListView,
  enableMapViewHideOption,
  providerList,
  isGroupedLocationDrawer = false,
  currentPage,
  drawerContent,
  isCheckboxDisabled,
  setSelectedCheckbox,
  addSelectedProvider,
  adobeLinkTrackEventCheckBox,
  setLastSelectedIndex,
  selectedCheckbox,
  compareShareToggle,
  setIsCompareClicked,
  maxBadgeCount,
  recommendationPreferences,
}: Props) => {
  const cardRef = useRef<HTMLElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const {
    providerName,
    primaryDegrees,
    address,
    phones,
    networkStatus,
    distance,
    providerId = '',
    speciality,
    locationId,
    healthGradeRating,
    organizationType = [''],
    organizationCode,
    providerType,
    isTieredProvider,
    website,
    npi = '',
    docasapPracticeId = '',
    networkEndDate,
    networkStartDate,
    acoProvider,
    recommendationDetails,
    networkId,
    virtualIndicator,
  } = practitioner;

  const { sectionType: sectionTypeStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const { choosePCP } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const nameAttrs = getNameAttrs(practitioner);

  const { searchFilters, setSearchFilters } = useContext(SearchFilterContext);

  const [isOpen, setIsOpen] = useState(false);
  const [isSmartChoiceOpen, setIsSmartChoiceOpen] = useState(false);

  const currentMember = getCurrentMember();

  const [
    showBestMatchModalFlag,
    showBestMatchModalEarlyPayFlag,
    virtualCareIndicatorEnabled,
    enableCost,
    enableRemoveEstimatedCostText,
    enableMapEnhancements,
    ispsxProviderRecommendationEnabled,
    enableRecommendationAuditModal,
    showNetworkInfo,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SHOW_BEST_MATCH_MODAL,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_BEST_MATCH_MODAL_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.VIRTUAL_CARE_INDICATOR_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REMOVE_ESTIMATED_COST_TEXT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PSX_PROVIDER_RECOMMENDATIONS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RECOMMENDATION_AUDIT_MODAL,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_PROVIDER_NETWORK_INFO,
  ]);

  const isCostEnabled = enableCost && !enableRemoveEstimatedCostText;

  const showBestMatchDetails = currentMember?.isIFPEarlyPay
    ? showBestMatchModalEarlyPayFlag
    : showBestMatchModalFlag;

  const [mapPinCoords] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS,
    []
  );

  const [selectedId, setSelectedId] = useSessionStorage<string | null>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  const [highlightId, setHighlightId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );

  if (
    providerId === highlightId?.providerId &&
    highlightId.from === 'map' &&
    scrollIntoView
  ) {
    scrollIntoView(index);
  }

  const setPinOnMap = () => {
    if (updatePin) {
      updatePin(practitioner);
    }
  };
  const name = parseProviderName(
    providerName || '',
    providerType as ProviderType,
    primaryDegrees?.[0],
    nameAttrs
  );

  const highlightClassName = `${
    providerId === highlightId?.providerId ? 'active' : ''
  }`;

  const sectionTypeDerived = sectionType || sectionTypeStore;
  const phone = phones?.phone;
  const appointment = phones?.appointment;
  const primaryPhone = getPrimaryPhone(phone, appointment);
  const suppressPatientReviews = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const coverageType = useCoverageType();

  const suppressCostEstimates = suppressCostEstimatesForPolicies(
    coverageType,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES_EMPIRE
  );
  const pageNumTxt = pageNumber ? `:page ${pageNumber}` : '';
  const linkLocationName = `body:${
    locationForAnalytics || ''
  } card${pageNumTxt}`;
  const mobileScreen = useMediaQuery(phoneOnly);
  const smallScreen = useMediaQuery(mobileOnly);
  const notXlScreen = useMediaQuery(notXlScreenOnly);

  useEffect(() => {
    if (navigateToDirections && selectedId === locationId) {
      handleDirectionClick?.();
    }
  }, [navigateToDirections]);

  useEffect(() => {
    if (!mobileRouteView && navigateToDirections) {
      setNavigateToDirections?.(false);
      setSelectedId(null);
    }
  }, [mobileRouteView]);

  const handleHoverOnCard = async () => {
    if (map?.current) {
      const marker = document.querySelector(`#custom-marker-${providerId}`);
      if (!enableMapEnhancements) {
        await setZoomLevel(map.current, mapPinCoords);
      } else {
        updateIconSize(true, marker, enableMapEnhancements);
      }

      setResultIcon(map.current, +providerId, true, setHighlightId, providerId);

      setPinOnMap();
    }
    setHighlightId({ providerId, from: usedAt });
  };

  const handleExitOnCard = async () => {
    if (enableMapEnhancements) {
      if (map?.current) {
        const marker = document.querySelector(`#custom-marker-${providerId}`);
        updateIconSize(false, marker, enableMapEnhancements);
      }
    }
  };

  const providerSpeciality = getProviderSpecialtyOrOrgTypeCode(
    providerType,
    organizationType,
    speciality
  );

  if (isLoading) {
    return (
      <DataCardSkeleton
        fromMobileListView={fromMobileListView}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        mobileScreen={isGroupedLocationDrawer ? smallScreen : mobileScreen}
        notXlScreen={notXlScreen}
      />
    );
  }

  if (
    providerType === ProviderType.ORGANIZATION &&
    website &&
    !address?.line?.[0]
  ) {
    return (
      <React.Fragment>
        <DataContainer
          cssProps={{
            enableMapEnhancements,
            enableMapViewHideOption,
          }}
        >
          {!enableMapViewHideOption && (
            <CardInfo
              cardType="data-card-with-link"
              headers={headers}
              indexForAnalytics={getLinkPositionIndex(index)}
              landingPage={false}
              locationForAnalytics={linkLocationName}
              onClosePopup={() => setIsOpen(false)}
              popupContainer={popupContainer}
              practitioner={practitioner}
              searchTerm={searchTerm}
              selectedFilters={selectedFilters}
            />
          )}
          <DataCardWithExternalLink
            cardRef={cardRef}
            css={css}
            enableMapEnhancements={enableMapEnhancements}
            enableMapViewHideOption={enableMapViewHideOption}
            expandedProviderListView={expandedProviderListView}
            facilityName={providerName || ''}
            headers={headers}
            highlightClassName={highlightClassName}
            index={index}
            linkLocationName={linkLocationName}
            networkStatus={networkStatus}
            practitioner={practitioner}
            providerId={providerId}
            providerSpeciality={providerSpeciality}
            selectedFilters={selectedFilters}
            setHighlightId={setHighlightId}
            updatePin={handleHoverOnCard}
            website={website}
          />
        </DataContainer>
      </React.Fragment>
    );
  }

  const chipStore = useChipStore(useShallow((state: ChipState) => state));

  const { chipValue } = chipStore;

  const isMedicalOrPrimaryCare = [
    CareCategories.ALL,
    CareCategories.MEDICAL_SPECIALISTS,
    CareCategories.PRIMARY_CARE,
  ].includes(chipValue as CareCategories);

  const isSmartChoice = useSmartChoice();
  const isSmartChoiceAllCategory = useSmartChoice(
    coverageType === 'B' ? false : true
  );

  const preferenceData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);

  return (
    <React.Fragment>
      <DataContainer
        cssProps={{
          enableMapEnhancements,
          enableMapViewHideOption,
        }}
        data-testid={`data-card-${sectionTypeDerived}-container`.replace(
          / /g,
          '-'
        )}
      >
        {isSmartChoice &&
        !popupContainer &&
        !expandedProviderListView &&
        !isGroupedLocationDrawer &&
        providerType?.toLowerCase() ===
          ProviderType.PRACTITIONER.toLowerCase() &&
        !enableMapViewHideOption &&
        parseInt(recommendationDetails?.totalRecommendationScore || '0') > 0 ? (
          <SmartChoiceScoreCard
            isAlternativeDataCard
            recommendationDetails={
              recommendationDetails as RecommendationDetails
            }
          />
        ) : null}
        {!isSmartChoiceAllCategory &&
          !expandedProviderListView &&
          !isGroupedLocationDrawer &&
          !enableMapViewHideOption && (
            <CardInfo
              cardType={badgeCardType}
              headers={headers}
              indexForAnalytics={getLinkPositionIndex(index)}
              landingPage={false}
              locationForAnalytics={linkLocationName}
              onClosePopup={() => setIsOpen(false)}
              popupContainer={popupContainer}
              practitioner={practitioner}
              searchTerm={searchTerm}
              selectedFilters={selectedFilters}
            />
          )}
        {isGroupedLocationDrawer ? (
          <GroupedPinDataCard
            acoProvider={acoProvider}
            addSelectedProvider={addSelectedProvider}
            address={address}
            adobeLinkTrackEventCheckBox={adobeLinkTrackEventCheckBox}
            badgeCardType={badgeCardType}
            cardRef={cardRef}
            choosePCP={Boolean(choosePCP)}
            compareShareToggle={compareShareToggle}
            currentPage={currentPage}
            distance={distance}
            docasapPracticeId={docasapPracticeId}
            drawerContent={drawerContent}
            enableCost={isCostEnabled}
            enableMapViewHideOption={enableMapViewHideOption}
            expandedProviderListView={expandedProviderListView}
            handleDirectionClick={handleDirectionClick}
            handleHoverOnCard={handleHoverOnCard}
            headers={headers}
            healthGradeRating={healthGradeRating}
            highlightId={highlightId}
            imgRef={imgRef}
            index={index}
            isCheckboxDisabled={isCheckboxDisabled}
            isGroupedLocationDrawer={isGroupedLocationDrawer}
            isMNR={isMNR}
            isTieredPlan={isTieredPlan}
            isTieredProvider={!!isTieredProvider}
            linkLocationName={linkLocationName}
            locationId={locationId}
            map={map}
            name={name}
            networkId={networkId}
            networkStatus={networkStatus}
            npi={npi}
            organizationCode={organizationCode}
            pageNumber={pageNumber}
            popupContainer={popupContainer}
            practitioner={practitioner}
            primaryPhone={primaryPhone}
            providerId={providerId}
            providerList={providerList}
            providerSpeciality={providerSpeciality}
            providerType={providerType}
            recommendationPreferences={recommendationPreferences}
            searchFilters={searchFilters}
            searchRadius={searchRadius}
            searchTerm={searchTerm}
            sectionTypeDerived={sectionTypeDerived}
            selectedCheckbox={selectedCheckbox}
            selectedFilters={selectedFilters}
            setHighlightId={setHighlightId}
            setIsCompareClicked={setIsCompareClicked}
            setIsOpen={setIsOpen}
            setLastSelectedIndex={setLastSelectedIndex}
            setNavigateToDirections={setNavigateToDirections}
            setRouteEndCoords={setRouteEndCoords}
            setSearchFilters={setSearchFilters}
            setSelectedCheckbox={setSelectedCheckbox}
            setSelectedId={setSelectedId}
            showBestMatchDetails={showBestMatchDetails}
            showSmartChoice={
              enableRecommendationAuditModal &&
              ispsxProviderRecommendationEnabled &&
              isMedicalOrPrimaryCare &&
              providerType === ProviderType.PRACTITIONER
            }
            speciality={speciality}
            suppressCostEstimates={suppressCostEstimates}
            suppressPatientReviews={suppressPatientReviews}
            surveyCount={practitioner.surveyCount}
            updatePin={updatePin}
            usedAt={usedAt}
            virtualCareIndicatorEnabled={virtualCareIndicatorEnabled}
            virtualIndicator={virtualIndicator || ''}
          />
        ) : (
          <DataCard
            acoProvider={acoProvider}
            addSelectedProvider={addSelectedProvider}
            address={address}
            adobeLinkTrackEventCheckBox={adobeLinkTrackEventCheckBox}
            badgeCardType={badgeCardType}
            cardContainerCss={css}
            cardRef={cardRef}
            choosePCP={Boolean(choosePCP)}
            compareShareToggle={compareShareToggle}
            currentPage={currentPage}
            distance={distance}
            docasapPracticeId={docasapPracticeId}
            drawerContent={drawerContent}
            enableCost={isCostEnabled}
            enableMapEnhancements={enableMapEnhancements}
            enableMapViewHideOption={enableMapViewHideOption}
            expandedProviderListView={expandedProviderListView}
            handleDirectionClick={handleDirectionClick}
            handleExitOnCard={handleExitOnCard}
            handleHoverOnCard={handleHoverOnCard}
            handleSmartChoiceClick={() => {
              setIsSmartChoiceOpen(true);
            }}
            headers={headers}
            healthGradeRating={healthGradeRating}
            highlightClassName={highlightClassName}
            highlightId={highlightId}
            imgRef={imgRef}
            index={index}
            isCheckboxDisabled={isCheckboxDisabled}
            isGroupedLocationDrawer={isGroupedLocationDrawer}
            isInNetworkBadge={isInNetworkBadge}
            isMNR={isMNR}
            isMapView={isMapView}
            isNetworkAlertIndicator={isNetworkAlertIndicator}
            isNetworkInfo={showNetworkInfo}
            isPopup={isPopup}
            isTieredPlan={isTieredPlan}
            isTieredProvider={!!isTieredProvider}
            linkLocationName={linkLocationName}
            locationId={locationId}
            map={map}
            maxBadgeCount={maxBadgeCount}
            name={name}
            networkEndDate={networkEndDate}
            networkId={networkId}
            networkStartDate={networkStartDate}
            networkStatus={networkStatus}
            npi={npi}
            organizationCode={organizationCode}
            pageNumber={pageNumber}
            popupContainer={popupContainer}
            practitioner={practitioner}
            primaryPhone={primaryPhone}
            providerId={providerId}
            providerList={providerList}
            providerSpeciality={providerSpeciality}
            providerType={providerType}
            recommendationPreferences={recommendationPreferences}
            searchFilters={searchFilters}
            searchRadius={searchRadius}
            searchTerm={searchTerm}
            sectionTypeDerived={sectionTypeDerived}
            selectedCheckbox={selectedCheckbox}
            selectedFilters={selectedFilters}
            setHighlightId={setHighlightId}
            setIsCompareClicked={setIsCompareClicked}
            setIsOpen={setIsOpen}
            setLastSelectedIndex={setLastSelectedIndex}
            setNavigateToDirections={setNavigateToDirections}
            setRouteEndCoords={setRouteEndCoords}
            setSearchFilters={setSearchFilters}
            setSelectedCheckbox={setSelectedCheckbox}
            setSelectedId={setSelectedId}
            showBestMatchDetails={showBestMatchDetails}
            showInternalCardInfo={isSmartChoiceAllCategory}
            showSmartChoice={
              enableRecommendationAuditModal &&
              ispsxProviderRecommendationEnabled &&
              isMedicalOrPrimaryCare &&
              providerType === ProviderType.PRACTITIONER &&
              isSmartChoice
            }
            speciality={speciality}
            suppressCostEstimates={suppressCostEstimates}
            suppressPatientReviews={suppressPatientReviews}
            surveyCount={practitioner.surveyCount}
            updatePin={updatePin}
            usedAt={usedAt}
            virtualCareIndicatorEnabled={virtualCareIndicatorEnabled}
            virtualIndicator={virtualIndicator || ''}
          />
        )}

        {showBestMatchDetails ? (
          <BestMatchDetails
            isOpen={isOpen}
            provider={practitioner}
            setIsOpen={setIsOpen}
          />
        ) : null}

        {isSmartChoiceOpen ? (
          <SmartChoiceDetails
            handleCloseDrawer={() => setIsSmartChoiceOpen(false)}
            isOpen={isSmartChoiceOpen}
            isTieredPlan={isTieredPlan}
            preferencesData={preferenceData}
            providerType={providerType}
            recommendationDetails={practitioner?.recommendationDetails}
            selectedChip={chipValue}
            stateCode={practitioner?.address?.state}
          />
        ) : null}
      </DataContainer>
    </React.Fragment>
  );
};
